import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../lib/tracking';

const RyadHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { t } = useLanguage();

  const trackPhoneClick = (source) => {
    trackEvent('Contact', {
      content_name: `Phone click - ${source}`,
      content_category: 'Phone',
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Detect active section
      const sections = ['projet', 'plans', 'disponibilites', 'video', 'simulation', 'vefa', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'projet', label: t.nav.project },
    { id: 'plans', label: t.nav.plans },
    { id: 'disponibilites', label: 'Disponibilités' },
    { id: 'video', label: t.nav.video },
    { id: 'simulation', label: 'Simulation' },
    { id: 'vefa', label: t.nav.vefa },
    { id: 'contact', label: t.nav.contact },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-black/5' 
        : 'bg-gradient-to-b from-black/50 to-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo & Title */}
          <div className="flex items-center space-x-4 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            {/* Logo */}
            <div className="relative">
              <div className={`absolute inset-0 bg-amber-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/ve5udi6l_400dpiLogo.png" 
                alt="SOGI SUARL Logo" 
                className="h-12 w-auto relative z-10 transform group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            
            {/* Title */}
            <div>
              <h1 className={`text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>
                {t.hero.title}
              </h1>
              <p className={`text-xs md:text-sm font-medium tracking-wider uppercase transition-colors duration-300 ${
                isScrolled ? 'text-amber-600' : 'text-amber-400'
              }`}>
                SOGI SUARL
              </p>
            </div>
          </div>

          {/* Navigation Desktop - Modern Pills */}
          <nav className="hidden lg:flex items-center">
            <div className={`flex items-center space-x-1 p-1.5 rounded-full transition-all duration-300 ${
              isScrolled ? 'bg-gray-100' : 'bg-white/10 backdrop-blur-sm'
            }`}>
              {navItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeSection === item.id
                      ? isScrolled 
                        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30' 
                        : 'bg-white text-gray-900'
                      : isScrolled 
                        ? 'text-gray-600 hover:text-amber-600 hover:bg-amber-50' 
                        : 'text-white/80 hover:text-white hover:bg-white/20'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Right Side - Phone & Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Phone CTA */}
            <a 
              href={`tel:${t.common.phone_senegal}`}
              onClick={() => trackPhoneClick('Header Desktop')}
              className={`hidden md:flex items-center space-x-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                isScrolled 
                  ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-lg shadow-amber-500/30' 
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm border border-white/30'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className="hidden xl:inline">{t.common.phone_senegal}</span>
              <span className="xl:hidden">Appeler</span>
            </a>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden p-3 rounded-xl transition-all duration-300 ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' 
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Slide Down */}
        <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          isMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="py-4 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100">
            {navItems.map((item, index) => (
              <button 
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`w-full text-left px-6 py-4 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-all duration-300 flex items-center justify-between group ${
                  activeSection === item.id ? 'bg-amber-50 text-amber-600 border-l-4 border-amber-500' : ''
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <span className="font-medium">{item.label}</span>
                <svg className="w-5 h-5 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            ))}
            
            {/* Mobile Phone CTA */}
            <div className="px-6 pt-4 mt-2 border-t border-gray-100">
              <a 
                href={`tel:${t.common.phone_senegal}`}
                onClick={() => trackPhoneClick('Header Mobile')}
                className="flex items-center justify-center space-x-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white py-4 rounded-xl font-semibold shadow-lg shadow-amber-500/30 hover:shadow-xl transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <span>{t.common.phone_senegal}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default RyadHeader;
